'use client';

import { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, FormInput, Sparkles, Plus, Trash2, 
  CheckCircle2, Star, Eye, Settings, BarChart2,
  Power, Search, ClipboardList, ExternalLink, MessageSquare, Edit2, Link as LinkIcon
} from 'lucide-react'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { toast } from 'sonner'

type FormField = {
  id: string
  type: 'text' | 'email' | 'rating' | 'choice'
  label: string
  placeholder?: string
  options?: string[]
}

interface DashboardForm {
  id: string
  title: string
  type: 'form' | 'poll'
  is_active: boolean
  view_count: number
  submission_count: number
  fields: FormField[]
  color: string
}

export default function FormBuilderClient() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'editor' | 'analytics'>('dashboard')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFormId, setSelectedFormId] = useState<string>('form-feedback')
  const [editingFormId, setEditingFormId] = useState<string | null>(null)

  // Dashboard forms state
  const [dashboardForms, setDashboardForms] = useState<DashboardForm[]>([
    {
      id: 'form-feedback',
      title: 'Customer Feedback Survey',
      type: 'form',
      is_active: true,
      view_count: 1489,
      submission_count: 432,
      color: '#3B82F6',
      fields: [
        { id: '1', type: 'text', label: 'Full Name', placeholder: 'Enter your name' },
        { id: '2', type: 'email', label: 'Business Email', placeholder: 'you@company.com' },
        { id: '3', type: 'rating', label: 'How would you rate our platform?' }
      ]
    },
    {
      id: 'form-priorities',
      title: 'Feature Prioritization Poll',
      type: 'poll',
      is_active: true,
      view_count: 890,
      submission_count: 312,
      color: '#EC4899',
      fields: [
        { id: '1', type: 'choice', label: 'What feature do you want next?', options: ['Custom Domains', 'Team Seats', 'Advanced API', 'Mobile App'] }
      ]
    },
    {
      id: 'form-newsletter',
      title: 'Weekly Newsletter Sign-Up',
      type: 'form',
      is_active: false,
      view_count: 452,
      submission_count: 114,
      color: '#8B5CF6',
      fields: [
        { id: '1', type: 'email', label: 'Email Address', placeholder: 'enter email...' }
      ]
    }
  ])

  // Current editor form inputs
  const [formTitle, setFormTitle] = useState('Customer Feedback Survey')
  const [fields, setFields] = useState<FormField[]>([
    { id: '1', type: 'text', label: 'Full Name', placeholder: 'Enter your name' },
    { id: '2', type: 'email', label: 'Business Email', placeholder: 'you@company.com' },
    { id: '3', type: 'rating', label: 'How would you rate our platform?' },
  ])
  const [newFieldType, setNewFieldType] = useState<'text' | 'email' | 'rating' | 'choice'>('text')
  const [newFieldLabel, setNewFieldLabel] = useState('')
  
  // Submit state for live form preview
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [ratingVal, setRatingVal] = useState(0)

  const handleToggleActive = (id: string) => {
    setDashboardForms(prev => prev.map(f => {
      if (f.id === id) {
        const nextStatus = !f.is_active
        toast.success(`Form "${f.title}" is now ${nextStatus ? 'active' : 'paused'}`)
        return { ...f, is_active: nextStatus }
      }
      return f
    }))
  }

  const handleDeleteForm = (id: string) => {
    setDashboardForms(prev => prev.filter(f => f.id !== id))
    toast.success('Form deleted from sandbox')
  }

  const handleCreateNewClick = () => {
    setEditingFormId(null)
    setFormTitle('New Custom Form')
    setFields([])
    setIsSubmitted(false)
    setActiveTab('editor')
  }

  const handleEditClick = (form: DashboardForm) => {
    setEditingFormId(form.id)
    setFormTitle(form.title)
    setFields(form.fields)
    setIsSubmitted(false)
    setActiveTab('editor')
  }

  const handleAddField = () => {
    if (!newFieldLabel.trim()) {
      toast.error('Please specify a label for the form field')
      return
    }

    const field: FormField = {
      id: Date.now().toString(),
      type: newFieldType,
      label: newFieldLabel,
      placeholder: newFieldType === 'text' ? 'Enter text...' : newFieldType === 'email' ? 'Enter email...' : undefined,
      options: newFieldType === 'choice' ? ['Excellent', 'Good', 'Average', 'Poor'] : undefined
    }

    setFields([...fields, field])
    setNewFieldLabel('')
    toast.success('Field added to editor preview!')
  }

  const handleDeleteField = (id: string) => {
    setFields(fields.filter(f => f.id !== id))
    toast.success('Field removed from editor preview')
  }

  const handleSaveForm = () => {
    if (!formTitle.trim()) {
      toast.error('Please enter a form title')
      return
    }

    if (editingFormId) {
      // Edit mode
      setDashboardForms(prev => prev.map(f => {
        if (f.id === editingFormId) {
          return {
            ...f,
            title: formTitle,
            fields: fields
          }
        }
        return f
      }))
      toast.success('Form changes saved to dashboard')
    } else {
      // Create mode
      const newForm: DashboardForm = {
        id: `form-${Date.now()}`,
        title: formTitle,
        type: fields.some(f => f.type === 'choice') ? 'poll' : 'form',
        is_active: true,
        view_count: 0,
        submission_count: 0,
        fields: fields,
        color: ['#3B82F6', '#EC4899', '#8B5CF6', '#10B981'][Math.floor(Math.random() * 4)]
      }
      setDashboardForms(prev => [...prev, newForm])
      toast.success('New form added to dashboard')
    }

    setActiveTab('dashboard')
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitted(true)
    
    // Dynamically update submission counts for simulated list if it matches
    setDashboardForms(prev => prev.map(f => {
      if (f.id === selectedFormId || (editingFormId && f.id === editingFormId)) {
        return {
          ...f,
          submission_count: f.submission_count + 1,
          view_count: f.view_count + 1
        }
      }
      return f
    }))

    toast.success('Mock response logged in dashboard!')
  }

  const selectedForm = dashboardForms.find(f => f.id === selectedFormId) || dashboardForms[0]

  const filteredForms = dashboardForms.filter(f => 
    f.title.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  }
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  }

  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30 text-white">
      <GeometricBackground />
      <MarketingNavbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6 max-w-[1400px] mx-auto z-10 flex flex-col items-center">
        <motion.div 
          className="text-center space-y-6 max-w-3xl"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="flex items-center gap-2 justify-center">
            <span className="px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
              Typeform Alternative
            </span>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter uppercase leading-[1.1]">
            Free Online Form Builder<br />
            <span className="text-gray-500">Create surveys & quizzes in</span><br />
            Seconds, code-free
          </motion.h1>

          <motion.p variants={itemVariants} className="text-base sm:text-lg text-white/50 font-light max-w-xl mx-auto leading-relaxed">
            Collect client details, gather viral quiz responses, design product feedback surveys, and view visual analytics summaries in one dashboard.
          </motion.p>
        </motion.div>

        {/* Dynamic Split Screen Sandbox */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="w-full max-w-5xl mt-12 bg-[#0A0A0A]/80 border border-white/10 rounded-3xl backdrop-blur-xl shadow-[0_0_80px_rgba(0,102,255,0.05)] overflow-hidden"
        >
          {/* Dashboard Header Bar */}
          <div className="px-6 py-4 border-b border-white/[0.08] flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white/[0.02] gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold uppercase tracking-wider">Forms Dashboard Workspace</span>
            </div>
            
            <button
              onClick={handleCreateNewClick}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-white text-white hover:text-black rounded-xl text-xs font-bold transition-all duration-300"
            >
              <Plus className="w-3.5 h-3.5" /> Create New
            </button>
          </div>

          {/* Workspace Tabs Navigation */}
          <div className="flex border-b border-white/[0.08] px-4 pt-3 bg-white/[0.01]">
            {[
              { id: 'dashboard', label: 'Forms Dashboard' },
              { id: 'editor', label: 'Form Editor' },
              { id: 'analytics', label: 'Responses & Analytics' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id === 'editor' && !editingFormId) {
                    handleCreateNewClick()
                  } else {
                    setActiveTab(tab.id as any)
                  }
                }}
                className={`flex-1 pb-3 text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-all border-b-2 ${
                  activeTab === tab.id 
                    ? 'text-white border-primary' 
                    : 'text-white/40 border-transparent hover:text-white/60'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Workspace Panels */}
          <div className="p-6">
            <AnimatePresence mode="wait">
              
              {/* ════ DASHBOARD VIEW ════ */}
              {activeTab === 'dashboard' && (
                <motion.div
                  key="form-dashboard-tab"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className="space-y-6"
                >
                  <div className="relative max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
                    <input
                      type="text"
                      placeholder="Search forms and polls..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 bg-white/[0.04] border border-white/[0.06] rounded-xl text-xs text-white placeholder-white/25 focus:outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredForms.map((form) => (
                      <div 
                        key={form.id} 
                        className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition-all flex flex-col overflow-hidden"
                      >
                        <div 
                          className="h-1 w-full opacity-60 group-hover:opacity-100 transition-opacity" 
                          style={{ backgroundColor: form.color }}
                        />
                        <div className="p-4 flex flex-col h-full space-y-4">
                          <div className="flex justify-between items-start">
                            <div className="p-2 rounded-xl bg-white/[0.04] text-primary border border-white/5">
                              {form.type === 'form' ? <ClipboardList className="w-4 h-4" /> : <BarChart2 className="w-4 h-4" />}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => handleToggleActive(form.id)}
                                className={`p-1.5 rounded-lg transition-all ${form.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-white/5 text-white/30'}`}
                                title={form.is_active ? 'Active' : 'Closed'}
                              >
                                <Power className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteForm(form.id)}
                                className="p-1.5 text-white/20 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          <div>
                            <h3 className="text-sm font-bold text-white group-hover:text-primary transition-colors line-clamp-1">{form.title}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[9px] font-mono uppercase tracking-wider text-white/30">{form.type}</span>
                              <div className="w-1 h-1 rounded-full bg-white/10" />
                              <span className="text-[9px] text-white/30">{form.is_active ? 'Accepting responses' : 'Paused'}</span>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 py-2 border-y border-white/[0.04] text-center">
                            <div>
                              <p className="text-[8px] font-mono text-white/20 uppercase">Total Views</p>
                              <p className="text-sm font-black text-white font-mono">{form.view_count.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-[8px] font-mono text-white/20 uppercase">Responses</p>
                              <p className="text-sm font-black text-primary font-mono">{form.submission_count.toLocaleString()}</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 pt-2">
                            <button
                              onClick={() => {
                                setSelectedFormId(form.id)
                                setActiveTab('analytics')
                              }}
                              className="flex items-center justify-center gap-1.5 py-2 bg-white/[0.03] hover:bg-white/[0.08] text-white/70 hover:text-white rounded-lg text-[10px] font-bold border border-white/[0.05] transition-all"
                            >
                              <MessageSquare className="w-3 h-3" /> Data
                            </button>
                            <button
                              onClick={() => handleEditClick(form)}
                              className="flex items-center justify-center gap-1.5 py-2 bg-white/[0.03] hover:bg-white/[0.08] text-white/70 hover:text-white rounded-lg text-[10px] font-bold border border-white/[0.05] transition-all"
                            >
                              <Edit2 className="w-3 h-3" /> Edit
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* ════ FORM EDITOR (BUILDER) ════ */}
              {activeTab === 'editor' && (
                <motion.div
                  key="form-editor-tab"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start animate-in fade-in duration-300"
                >
                  {/* Left panel: builder config */}
                  <div className="lg:col-span-6 space-y-6">
                    <div className="space-y-2">
                      <label className="text-xs font-mono text-white/40 uppercase">Survey Title</label>
                      <input
                        type="text"
                        value={formTitle}
                        onChange={(e) => setFormTitle(e.target.value)}
                        className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm focus:outline-none focus:border-primary/60 transition-all font-light"
                      />
                    </div>

                    <div className="space-y-3">
                      <label className="text-xs font-mono text-white/40 uppercase">Configured Fields</label>
                      <div className="space-y-2">
                        {fields.map((field) => (
                          <div key={field.id} className="flex items-center justify-between p-3 bg-white/[0.02] border border-white/5 rounded-xl">
                            <div>
                              <p className="text-xs font-bold text-white/90">{field.label}</p>
                              <span className="text-[8px] font-mono text-primary bg-primary/10 px-1.5 py-0.5 rounded-full uppercase mt-1 inline-block">
                                {field.type}
                              </span>
                            </div>
                            <button 
                              onClick={() => handleDeleteField(field.id)}
                              className="p-1.5 text-white/30 hover:text-red-400 hover:bg-white/5 rounded-lg transition-all"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                        {fields.length === 0 && (
                          <p className="text-xs text-white/20 py-4 text-center">No fields added yet.</p>
                        )}
                      </div>
                    </div>

                    {/* Add Field widget */}
                    <div className="p-4 bg-white/[0.01] border border-white/5 rounded-2xl space-y-3">
                      <p className="text-[10px] font-mono text-white/40 uppercase">Insert Field Block</p>
                      
                      <div className="grid grid-cols-2 gap-2">
                        {([
                          { id: 'text', label: 'Short Text' },
                          { id: 'email', label: 'Email Address' },
                          { id: 'rating', label: 'Rating Stars' },
                          { id: 'choice', label: 'Multiple Choice' }
                        ] as const).map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setNewFieldType(opt.id)}
                            className={`py-2 px-3 rounded-lg text-[10px] font-bold border transition-all ${
                              newFieldType === opt.id 
                                ? 'border-primary bg-primary/15 text-white' 
                                : 'border-white/5 bg-white/[0.02] text-white/50 hover:border-white/10'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>

                      <input
                        type="text"
                        placeholder="Field Question Label"
                        value={newFieldLabel}
                        onChange={(e) => setNewFieldLabel(e.target.value)}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs focus:outline-none focus:border-primary/60 transition-all"
                      />

                      <button
                        type="button"
                        onClick={handleAddField}
                        className="w-full py-2 bg-white text-black hover:bg-white/90 font-bold rounded-lg text-xs transition-all flex items-center justify-center gap-1"
                      >
                        <Plus className="w-3.5 h-3.5" /> Insert Field
                      </button>
                    </div>

                    {/* Bottom Save options */}
                    <div className="flex gap-2 pt-4 border-t border-white/5">
                      <button
                        onClick={handleSaveForm}
                        className="flex-1 py-3 bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs transition-all"
                      >
                        Save Form & Back
                      </button>
                      <button
                        onClick={() => setActiveTab('dashboard')}
                        className="py-3 px-5 bg-white/5 hover:bg-white/10 text-white rounded-xl text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>

                  {/* Right panel: mobile preview */}
                  <div className="lg:col-span-6 bg-background/30 border border-white/10 rounded-3xl p-6 backdrop-blur-xl flex flex-col justify-between min-h-[400px]">
                    <div className="flex items-center justify-between border-b border-white/5 pb-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-white/40" />
                        <span className="text-[10px] font-mono text-white/40 uppercase">Live Mobile Preview</span>
                      </div>
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                    </div>

                    {!isSubmitted ? (
                      <form onSubmit={handleFormSubmit} className="space-y-4 flex-1">
                        <h3 className="text-sm font-bold text-white uppercase tracking-tight">{formTitle || 'Untitled Form'}</h3>
                        
                        {fields.map((field) => (
                          <div key={field.id} className="space-y-1.5 text-left">
                            <label className="text-[11px] font-semibold text-white/70">{field.label}</label>
                            {field.type === 'text' && (
                              <input
                                type="text"
                                placeholder={field.placeholder}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs focus:outline-none"
                              />
                            )}
                            {field.type === 'email' && (
                              <input
                                type="email"
                                placeholder={field.placeholder}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs focus:outline-none"
                              />
                            )}
                            {field.type === 'rating' && (
                              <div className="flex gap-1.5 items-center text-yellow-500">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <button
                                    key={star}
                                    type="button"
                                    onClick={() => setRatingVal(star)}
                                    className="focus:outline-none"
                                  >
                                    <Star className={`w-5 h-5 ${star <= ratingVal ? 'fill-current' : 'text-white/20'}`} />
                                  </button>
                                ))}
                              </div>
                            )}
                            {field.type === 'choice' && (
                              <div className="grid grid-cols-2 gap-2">
                                {field.options?.map((opt) => (
                                  <div 
                                    key={opt}
                                    className="p-2 bg-white/[0.02] border border-white/15 hover:border-primary/50 rounded-lg text-center text-[10px] text-white/60 hover:text-white cursor-pointer select-none"
                                  >
                                    {opt}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}

                        {fields.length === 0 && (
                          <p className="text-xs text-white/25 text-center py-8">Form fields will preview here.</p>
                        )}

                        <button
                          type="submit"
                          disabled={fields.length === 0}
                          className="w-full py-2.5 bg-primary text-white font-bold rounded-lg text-xs transition-all disabled:opacity-50 mt-4"
                        >
                          Submit Form
                        </button>
                      </form>
                    ) : (
                      <div className="py-10 flex flex-col items-center justify-center text-center space-y-4">
                        <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                          <CheckCircle2 className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-white">Mock Response Submitted!</p>
                          <p className="text-[10px] text-white/40 mt-1">Visit responses tab to see results.</p>
                        </div>
                        <button
                          onClick={() => {
                            setIsSubmitted(false)
                            setRatingVal(0)
                          }}
                          className="px-3 py-1.5 bg-white/5 border border-white/10 text-white rounded-lg text-[10px]"
                        >
                          Fill Again
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* ════ RESPONSES & ANALYTICS ════ */}
              {activeTab === 'analytics' && (
                <motion.div
                  key="form-analytics-tab"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-6"
                >
                  <div className="flex justify-between items-center border-b border-white/5 pb-3">
                    <div>
                      <h3 className="text-sm font-bold text-white uppercase">{selectedForm.title}</h3>
                      <p className="text-[10px] text-white/40 mt-0.5">Submission analytics summary</p>
                    </div>
                    <button
                      onClick={() => setActiveTab('dashboard')}
                      className="px-3 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-white"
                    >
                      Back to Dashboard
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-4 bg-white/[0.01] border border-white/5 rounded-2xl">
                      <p className="text-[9px] font-mono text-white/40 uppercase">Total Views</p>
                      <p className="text-lg font-black text-white font-mono">{selectedForm.view_count.toLocaleString()}</p>
                    </div>
                    <div className="p-4 bg-white/[0.01] border border-white/5 rounded-2xl">
                      <p className="text-[9px] font-mono text-white/40 uppercase">Total Submissions</p>
                      <p className="text-lg font-black text-primary font-mono">{selectedForm.submission_count.toLocaleString()}</p>
                    </div>
                    <div className="p-4 bg-white/[0.01] border border-white/5 rounded-2xl">
                      <p className="text-[9px] font-mono text-white/40 uppercase">Conversion Rate</p>
                      <p className="text-lg font-black text-emerald-400 font-mono">
                        {selectedForm.view_count > 0 
                          ? `${Math.round((selectedForm.submission_count / selectedForm.view_count) * 100)}%` 
                          : '0%'}
                      </p>
                    </div>
                  </div>

                  {/* Dynamic analytics detail */}
                  <div className="p-5 bg-white/[0.01] border border-white/5 rounded-2xl">
                    <p className="text-xs font-semibold text-white/80 flex items-center gap-1.5 mb-4">
                      <BarChart2 className="w-4 h-4 text-primary" /> Visualized Results Breakdown
                    </p>

                    {selectedForm.fields.length === 0 ? (
                      <p className="text-xs text-white/20 py-4 text-center">No fields configured to analyze.</p>
                    ) : (
                      <div className="space-y-4">
                        {selectedForm.fields.map((f, idx) => (
                          <div key={f.id || idx} className="space-y-2 border-t border-white/[0.04] pt-4 first:border-0 first:pt-0">
                            <p className="text-xs font-semibold text-white/70">{f.label}</p>
                            
                            {f.type === 'rating' && (
                              <div className="space-y-1.5 font-mono text-[11px] text-white/50">
                                {[5, 4, 3, 2, 1].map((stars) => {
                                  // Fake distribution
                                  const scalePct = stars === 5 ? 65 : stars === 4 ? 20 : stars === 3 ? 10 : 3
                                  return (
                                    <div key={stars} className="flex items-center gap-2">
                                      <span className="w-12">{stars} Stars</span>
                                      <div className="flex-1 h-2 bg-white/[0.03] rounded-full overflow-hidden">
                                        <div className="h-full bg-primary" style={{ width: `${scalePct}%` }} />
                                      </div>
                                      <span className="w-8 text-right">{scalePct}%</span>
                                    </div>
                                  )
                                })}
                              </div>
                            )}

                            {f.type === 'choice' && (
                              <div className="space-y-1.5 font-mono text-[11px] text-white/50">
                                {(f.options || ['Choice A', 'Choice B']).map((opt, oIdx) => {
                                  const scalePct = oIdx === 0 ? 55 : oIdx === 1 ? 25 : oIdx === 2 ? 15 : 5
                                  return (
                                    <div key={opt} className="flex items-center gap-2">
                                      <span className="w-24 truncate">{opt}</span>
                                      <div className="flex-1 h-2 bg-white/[0.03] rounded-full overflow-hidden">
                                        <div className="h-full bg-primary" style={{ width: `${scalePct}%` }} />
                                      </div>
                                      <span className="w-8 text-right">{scalePct}%</span>
                                    </div>
                                  )
                                })}
                              </div>
                            )}

                            {(f.type === 'text' || f.type === 'email') && (
                              <div className="bg-white/[0.01] border border-white/[0.06] rounded-xl p-3 font-mono text-[10px] text-white/40 space-y-1.5">
                                <p className="uppercase text-[8px] tracking-wider text-white/20 mb-2">Recent mock answers</p>
                                <p>• clark.kent@dailyplanet.com</p>
                                <p>• bruce.wayne@waynecorp.com</p>
                                <p>• diana.prince@themyscira.org</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="py-20 border-t border-white/5 relative z-10">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <FormInput className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Conditional Logic Rules</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Route respondents dynamically. Show or hide questions based on previous selections to create clean, personalized survey flows.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Lead Capturing Sync</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Integrate your forms directly with marketing email triggers, Webhooks, or Slack notifications to alert your team immediately.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <BarChart2 className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Visual Chart Summaries</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Skip CSV exports. View answers as beautiful auto-generated bar charts, pie graphs, and rating summaries inside your portal.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-white/5 text-center relative z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.05),transparent_70%)] blur-[100px] pointer-events-none" />
        <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">Gather beautiful feedback</h2>
        <p className="text-white/40 text-sm max-w-md mx-auto mt-4 font-light">Create gorgeous forms, quizzes, and polls with high-converting layouts and deep analytics.</p>
        <Link 
          href="/auth/sign-up" 
          className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 bg-primary text-white font-bold rounded-full hover:scale-105 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] active:scale-95"
        >
          Build a Free Form <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      <MarketingFooter />
    </div>
  )
}
